import { fromCallback } from 'xstate';

import { AppointmentFileTypesLookup } from '@eluve/graphql-types';
import { getAudioConstraints } from '@eluve/mic-utils';
import {
  getOpfsFileSize,
  getTabId,
  recordAppointmentManifestFile,
  updateAppointmentManifestFileStats,
} from '@eluve/user-local-files';
import { isAudioFileChunkFile } from '@eluve/utils';

import type {
  AudioSource,
  AudioSourceConfig,
} from '../audio-sources/audio-source';
import { BlobAudioSource } from '../audio-sources/blob-audio-source';
import { PcmAudioSource } from '../audio-sources/pcm-audio-source';
import { DEFAULT_OPUS_BITRATE } from '../opus-bitrate-options';
import { PageVisibilityMonitor } from '../page-visibility-monitor';
import { MsgFromMain, MsgToMain } from '../workers/messages-types';

import { type AudioFileSystemEvents } from './audioFileSystem.types';
import {
  type MediaRecorderEvents,
  MediaRecorderInput,
} from './mediaRecorder.types';
import { AudioHealthCheck } from './periodicAudioHealthCheck';

// Check if AudioEncoder is supported (for fallback detection)
const isAudioEncoderSupported = (): boolean => {
  return typeof AudioEncoder !== 'undefined';
};

// How long recording start may wait for the encoder worker's `ready` message
// before failing the session. Worker boot is OPFS + logger init (sub-second in
// practice); the generous bound absorbs slow machines without letting a dead
// worker record silently. [ENG-5289]
const WORKER_READY_TIMEOUT_MS = 15_000;

// Bound on the worker 'stop' → 'stopped' finalize handshake. A worker hung in
// init never processes 'stop' (its message queue is serialized), so an
// unbounded wait would keep cleanup from ever reaching terminate(). Generous
// because a healthy finalize can legitimately include slow-network resumable
// upload tails that must not be truncated. [ENG-5289]
const WORKER_STOP_TIMEOUT_MS = 30_000;

export const webWorkerAudioActor = fromCallback<
  MediaRecorderEvents,
  MediaRecorderInput
>(({ input, sendBack, receive }) => {
  const safeSendBack = sendBack as (
    event: MediaRecorderEvents | AudioFileSystemEvents,
  ) => void;

  const {
    audioPacketSubject,
    deviceId,
    logger,
    MuteDetector,
    tenantId,
    userId,
    db,
    recordingStartedAt,
    appointmentId = '',
    chunkDurationInSeconds,
    channelCount = 1,
    initialFileName,
    resumableUploadSessionUri,
    nextChunkResumableUploadSessionUri,
    datadogEnv,
    serviceName,
    supportedAudioFormat,
    useBlobMode,
    opusBitrate = DEFAULT_OPUS_BITRATE,
  } = input;

  // Determine which mode to use. When useBlobMode is provided (web app),
  // use it directly. Otherwise fall back to internal detection (extension).
  const mode: 'pcm' | 'blob' =
    (useBlobMode ?? !isAudioEncoderSupported()) ? 'blob' : 'pcm';

  logger('info', `Web Worker Audio Actor initializing in ${mode} mode`, {
    useBlobMode,
    hasAudioEncoder: typeof AudioEncoder !== 'undefined',
  });

  // Create a new worker instance for this recording session. Construction can
  // throw synchronously (e.g. CSP SecurityError, resource exhaustion) — route
  // that through recorder.error like every other boot failure rather than
  // crashing the callback actor, whose parent invoke has no onError. [ENG-5289]
  let constructedWorker: Worker | null = null;
  try {
    constructedWorker = new Worker(
      new URL('../workers/audio-encoder.worker.ts', import.meta.url),
      {
        type: 'module',
        name: 'audio-encoder-worker',
      },
    );
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger('error', 'Failed to construct audio encoder worker', {
      errorMessage: err.message,
    });
    safeSendBack({
      type: 'recorder.error',
      error: new Error(
        `Failed to construct audio encoder worker: ${err.message}`,
      ),
    });
  }
  if (!constructedWorker) {
    return;
  }
  const audioEncoderWorker = constructedWorker;

  // A Worker whose script fails to load (e.g. stale tab 404ing on a hashed
  // chunk after a deploy) or crashes during module evaluation never throws —
  // it fires this async `error` event, and the same event fires for
  // mid-session unhandled exceptions. Without a listener the worker dies
  // silently while both server audio paths starve. [ENG-5289]
  let workerFailed = false;
  let workerBootError: Error | null = null;
  let workerReadyObserved = false;
  let settleWorkerReady: {
    resolve: () => void;
    reject: (error: Error) => void;
  } | null = null;

  const workerErrorHandler = (event: ErrorEvent) => {
    const alreadyFailed = workerFailed;
    workerFailed = true;
    const error = new Error(
      `Audio encoder worker error: ${event.message || 'worker script failed to load or evaluate'}`,
    );
    logger('error', 'Audio encoder worker fired an error event', {
      errorMessage: event.message,
      filename: event.filename,
      lineno: event.lineno,
      workerReadyObserved,
    });
    // One recorder.error per actor lifetime — a crashing worker can fire an
    // error event per unhandled exception, and duplicates would race the
    // machine's RecordingFailed transition and burn its single retry.
    // Mirrors the disconnectSent guard (ENG-4299).
    if (alreadyFailed) {
      return;
    }
    if (settleWorkerReady) {
      // Boot-phase failure — fail the pending ready gate; startRecording
      // emits the single recorder.error.
      settleWorkerReady.reject(error);
      settleWorkerReady = null;
    } else if (workerReadyObserved) {
      // Mid-session crash — route into the same RecordingFailed flow.
      safeSendBack({ type: 'recorder.error', error });
    } else {
      // Failed before the ready gate exists — stored so the gate rejects
      // immediately when startRecording reaches it.
      workerBootError = error;
    }
  };
  audioEncoderWorker.addEventListener('error', workerErrorHandler);

  let audioSource: AudioSource | null = null;

  let mediaStream: MediaStream | null = null;

  let numAudioPacketsReceived = 0;
  let totalBytesReceived = 0;

  // [ENG-4717] Per-chunk manifest diagnostics. Packets/timestamps for the
  // current chunk are the delta since the previous chunk finalized; byteSize is
  // read from the closed OPFS file at chunk-finalized.
  let chunkStartPackets = 0;
  let chunkFirstPacketAt: string | null = null;

  // Degraded-backup recovery: the raw write-ahead backup registered by
  // 'backup-file-started', so 'chunking-degraded' can flip it to the segment's
  // authoritative audio. The registration promise serializes the degrade
  // update behind the row add; the pending flag buffers a degrade signal that
  // races ahead of 'backup-file-started'.
  let backupFileName: string | null = null;
  let backupRowRegistered: Promise<unknown> = Promise.resolve();
  let backupDegradePending = false;

  let recordingStarted = false;
  let isMuted = false;
  let muteStartTime: number | null = null;

  let isFinishedStop = false;
  let visibilityMonitor: PageVisibilityMonitor | null = null;

  if (!db) {
    safeSendBack({
      type: 'recorder.error',
      error: new Error(
        'Dexie DB instance is required for web worker audio actor',
      ),
    });

    return;
  }

  const audioHealthCheck = new AudioHealthCheck({
    logger,
    getTotalBytesReceived: () => totalBytesReceived,
    getCaptureProgress: () => audioSource?.getCaptureProgress() ?? null,
    getMediaStream: () => mediaStream,
    onHealthCheckFailed: (reason: string) => {
      logger(
        'warn',
        'Audio health check failed — treating as microphone disconnect',
        { reason },
      );
      onMicrophoneDisconnectDetected();
    },
    onHealthyAudioObserved: () => {
      safeSendBack({ type: 'microphone.successfully_checked_audio' });
    },
    getAdditionalLogData: () => ({
      hasAudioSource: Boolean(audioSource),
      audioContextState: audioSource?.getAudioContextState() ?? null,
      numAudioPacketsReceived,
      isMuted,
      muteStartTime,
      recordingStarted,
      mode,
    }),
    isHealthy: () => {
      if (!mediaStream) {
        return { healthy: false, reason: 'MediaStream is missing' };
      }
      const tracks = mediaStream.getAudioTracks();
      if (tracks.length === 0) {
        return { healthy: false, reason: 'MediaStream has no audio tracks' };
      }
      const liveTracks = tracks.filter((t) => t.readyState === 'live');
      if (liveTracks.length === 0) {
        const states = tracks.map((t) => t.readyState).join(',');
        return {
          healthy: false,
          reason: `All audio tracks have ended (states: ${states})`,
        };
      }
      const enabledLiveTracks = liveTracks.filter((t) => t.enabled);
      if (enabledLiveTracks.length === 0) {
        return {
          healthy: false,
          reason: 'All live audio tracks are disabled',
        };
      }
      return { healthy: true };
    },
  });

  // Callback for audio sources to report bytes received
  const onBytesReceived = (bytes: number) => {
    numAudioPacketsReceived += 1;
    totalBytesReceived += bytes;
    if (chunkFirstPacketAt === null) {
      chunkFirstPacketAt = new Date().toISOString();
    }
  };

  // The remuxer failed: chunks stopped and the raw write-ahead backup is now
  // the segment's only complete audio. Flip the Dexie row (so recovery sweeps
  // upload it instead of discarding it as redundant) and name it in the
  // durable manifest (so server reconciliation protects it instead of
  // cancelling it as a phantom). Best-effort — must never break recording.
  const markBackupDegraded = () => {
    const fileName = backupFileName;
    if (!fileName) {
      // 'chunking-degraded' raced ahead of 'backup-file-started' — apply once
      // the backup registers.
      backupDegradePending = true;
      return;
    }

    void backupRowRegistered
      .catch(() => undefined)
      .then(async () => {
        const updated = await db.appointmentFiles.update(fileName, {
          wasDegraded: true,
        });
        if (updated === 0) {
          // Registration never landed — upsert so the recovery sweeps still
          // see a degraded backup row rather than nothing.
          await db.appointmentFiles.put({
            id: fileName,
            appointmentId,
            attemptCount: 0,
            lastUploadAttemptedAt: new Date().toISOString(),
            wasDegraded: true,
            shouldBeTranscribed: true,
            uploadType: AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
            ownedByTabId: getTabId(),
            writeState: 'recording',
            isWriteAheadBackup: true,
          });
        }
      })
      .catch((err) => {
        logger('warn', 'Error marking backup file degraded in DB', {
          fileName,
          appointmentId,
          error: err instanceof Error ? err.message : String(err),
        });
      });

    // The backup is a whole-segment file, not a chunk — chunkNum null matches
    // the batch-mode manifest convention.
    recordAppointmentManifestFile(db, appointmentId, {
      fileName,
      recordingStartedAt,
      chunkNum: null,
      wasDegraded: true,
    }).catch((err) => {
      logger('warn', 'Error recording degraded backup in manifest', {
        fileName,
        appointmentId,
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  const workerMessageHandler = (e: MessageEvent<MsgToMain>) => {
    const msg = e.data;

    if (msg.type === 'ready') {
      logger('info', 'Worker ready');
      recordingStarted = true;
      workerReadyObserved = true;
      settleWorkerReady?.resolve();
      settleWorkerReady = null;
    } else if (msg.type === 'rolling-to-new-chunk') {
      safeSendBack({
        type: 'fileSystem.rollingToNewChunk',
        completedChunkName: msg.completedChunkName,
      });
    } else if (msg.type === 'backup-file-started') {
      // Raw write-ahead backup (blob+chunked): Dexie-only, uploaded only if a
      // crash strands it. No machine fileCreated — must not pend completion.
      backupFileName = msg.fileName;
      backupRowRegistered = db.appointmentFiles
        .add({
          id: msg.fileName,
          appointmentId,
          attemptCount: 0,
          lastUploadAttemptedAt: new Date().toISOString(),
          wasDegraded: false,
          shouldBeTranscribed: true,
          uploadType: AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
          ownedByTabId: getTabId(),
          writeState: 'recording',
          isWriteAheadBackup: true,
        })
        .then(() =>
          logger('info', 'Registered raw write-ahead backup file', {
            fileName: msg.fileName,
            appointmentId,
          }),
        )
        .catch((err) => {
          logger('warn', 'Error registering raw backup file in DB', {
            fileName: msg.fileName,
            appointmentId,
            error: err instanceof Error ? err.message : String(err),
          });
        });
      if (backupDegradePending) {
        backupDegradePending = false;
        markBackupDegraded();
      }
    } else if (msg.type === 'chunking-degraded') {
      logger('warn', 'Worker chunk remux degraded to raw batch capture', {
        appointmentId,
      });
      markBackupDegraded();
      safeSendBack({ type: 'fileSystem.chunkingDegraded' });
    } else if (msg.type === 'chunk-started') {
      const { fileName, chunkNum } = msg;
      logger('info', 'Chunk started', { fileName, chunkDurationInSeconds });

      const idbStartedAt = performance.now();
      db.appointmentFiles
        .add({
          id: msg.fileName,
          appointmentId,
          attemptCount: 0,
          lastUploadAttemptedAt: new Date().toISOString(),
          wasDegraded: false,
          shouldBeTranscribed: true,
          uploadType: isAudioFileChunkFile(msg.fileName)
            ? AppointmentFileTypesLookup.CHUNKED_AUDIO
            : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
          ownedByTabId: getTabId(),
          // Worker is actively writing this chunk — the sync machine must not
          // pick it up until it flips to 'final' (chunk-finalized below).
          writeState: 'recording',
        })
        .then(() =>
          logger('info', `Added file '${msg.fileName}' to DB`, {
            fileName: msg.fileName,
            appointmentId,
            idbDurationMs: Math.round(performance.now() - idbStartedAt),
          }),
        )
        .catch((err) => {
          // TODO(jesse)[ENG-1563]: Better error handling but this should be picked
          // up again by the periodic sync and reconciliation logic
          logger('warn', 'Error adding chunk to DB:', {
            fileName: msg.fileName,
            appointmentId,
            idbDurationMs: Math.round(performance.now() - idbStartedAt),
            error: err instanceof Error ? err.message : String(err),
          });
        });

      // [ENG-4717] Durable manifest entry — survives the post-upload deletion of
      // this file so it can be sent to the server at completion.
      recordAppointmentManifestFile(db, appointmentId, {
        fileName: msg.fileName,
        recordingStartedAt,
        chunkNum,
      }).catch((err) => {
        logger('warn', 'Error recording manifest file:', {
          fileName: msg.fileName,
          error: err instanceof Error ? err.message : String(err),
        });
      });

      safeSendBack({
        type: 'fileSystem.ready',
      });

      safeSendBack({
        type: 'fileSystem.fileCreated',
        fileName,
        chunkNum,
      });
    } else if (msg.type === 'chunk-finalized') {
      const { chunkNum, fileName } = msg;

      // Chunk closed in OPFS — safe for the sync machine to upload. Best
      // effort: if the update is lost, the page-load sweep flips stale
      // own-tab 'recording' rows to 'final'.
      db.appointmentFiles
        .update(fileName, { writeState: 'final' })
        .catch((err) => {
          logger('warn', 'Error marking chunk final in DB', {
            fileName,
            error: err instanceof Error ? err.message : String(err),
          });
        });

      // [ENG-4717] Stamp finalize-time diagnostics onto the manifest entry.
      // packetCount + write window are this chunk's delta; byteSize is the now-
      // closed OPFS file's size. Best-effort, off the recording path.
      const packetCount = numAudioPacketsReceived - chunkStartPackets;
      const firstPacketAt = chunkFirstPacketAt;
      const lastPacketAt = new Date().toISOString();
      chunkStartPackets = numAudioPacketsReceived;
      chunkFirstPacketAt = null;

      void getOpfsFileSize(
        [userId, tenantId, 'appointments', appointmentId],
        fileName,
      )
        .then((byteSize) =>
          updateAppointmentManifestFileStats(db, appointmentId, {
            fileName,
            recordingStartedAt,
            chunkNum,
            byteSize,
            packetCount,
            firstPacketAt,
            lastPacketAt,
          }),
        )
        .catch((err) => {
          logger('warn', 'Failed to update manifest file stats', {
            fileName,
            error: err instanceof Error ? err.message : String(err),
          });
        });

      safeSendBack({
        type: 'fileSystem.fileReady',
        fileName,
        chunkNum,
      });
    } else if (msg.type === 'error') {
      // Preserve the originating failure's name (e.g. 'QuotaExceededError')
      // on the rebuilt Error so the machine can guard on `error.name` rather
      // than threading a parallel discriminator field through every event.
      const err = new Error(msg.message);
      if (msg.errorName) {
        err.name = msg.errorName;
      }
      safeSendBack({
        type: 'recorder.error',
        error: err,
      } satisfies MediaRecorderEvents);
    }
  };

  // A single failure can trip several producers (health check, track `ended`,
  // page-visibility interruption). The parent machine re-enters `Paused` with
  // `reenter: true`, so each duplicate `microphone.disconnected` re-runs
  // `onPaused` and double-counts elapsed recording time. Guard to one
  // disconnect per actor lifetime — the actor is recreated on resume, so the
  // next attempt gets a fresh guard. Mirrors `sendDisconnectOnce` in the legacy
  // `mediaRecorder` actor. See ENG-4299.
  let disconnectSent = false;
  const onMicrophoneDisconnectDetected = () => {
    if (disconnectSent) return;
    disconnectSent = true;
    safeSendBack({ type: 'microphone.disconnected' });
  };

  const handleMuteEvent = () => {
    isMuted = true;
    muteStartTime = Date.now();

    logger('warn', 'Microphone is muted.', {
      deviceId,
      totalBytesReceived: totalBytesReceived,
      numAudioPacketsReceived: numAudioPacketsReceived,
    });
    safeSendBack({ type: 'microphone.muted' });
  };

  const handleUnmuteEvent = () => {
    isMuted = false;
    muteStartTime = null;
    logger('info', 'Microphone is unmuted.', {
      deviceId,
      totalBytesReceived: totalBytesReceived,
      numAudioPacketsReceived: numAudioPacketsReceived,
    });
    safeSendBack({ type: 'microphone.unmuted' });
  };

  const stopRecording = async () => {
    audioHealthCheck.stop();
    visibilityMonitor?.stop();
    visibilityMonitor = null;

    // 1. Stop the media stream FIRST - prevents new audio from entering
    try {
      mediaStream?.getTracks().forEach((track) => {
        track.stop();
      });
    } catch (e) {
      logger('warn', 'Error stopping media stream tracks', {
        error: e,
      });
    }

    // Stop all tracked media streams (including waveform clones)
    window.eluveMediaStreams.stopAll();

    // 2. Stop the audio source (handles mode-specific cleanup)
    const captureGapStats = audioSource?.getCaptureGapStats() ?? {
      captureGapCount: 0,
      totalCaptureGapMs: 0,
      peakDeliveryLagMs: 0,
      droppedFrameCount: 0,
    };
    if (audioSource) {
      await audioSource.stop();
      audioSource = null;
    }

    // 3. Wait for worker to finalize encoding - safe because no more data can arrive
    if (workerFailed) {
      // A dead worker never replies 'stopped' — waiting would hang cleanup
      // (and the machine's Finalizing wait) on a message that cannot arrive.
      // [ENG-5289]
      logger('warn', 'Skipping encoder worker stop handshake: worker failed');
    } else {
      let resolveWorkerStopped: (() => void) | null = null;
      const workerStopped = new Promise<void>((resolve) => {
        resolveWorkerStopped = resolve;
      });
      const stoppedHandler = (e: MessageEvent<MsgToMain>) => {
        if (e.data.type === 'stopped') {
          logger('info', 'Worker stopped');
          resolveWorkerStopped?.();
        }
      };
      audioEncoderWorker.addEventListener('message', stoppedHandler);

      // Bounded: a worker hung in init never processes 'stop', and a worker
      // that dies mid-finalize never replies — cleanup must still reach
      // terminate(). [ENG-5289]
      let stopTimer: ReturnType<typeof setTimeout> | null = null;
      try {
        audioEncoderWorker.postMessage({ type: 'stop' } satisfies MsgFromMain);
        await Promise.race([
          workerStopped,
          new Promise<void>((resolve) => {
            stopTimer = setTimeout(() => {
              logger('warn', 'Encoder worker stop handshake timed out', {
                timeoutMs: WORKER_STOP_TIMEOUT_MS,
              });
              resolve();
            }, WORKER_STOP_TIMEOUT_MS);
          }),
        ]);
      } catch (e) {
        logger('error', 'Error stopping worker', {
          error: e,
        });
      } finally {
        if (stopTimer) {
          clearTimeout(stopTimer);
        }
        audioEncoderWorker.removeEventListener('message', stoppedHandler);
      }
    }

    // 4. Terminate worker to release resources
    try {
      audioEncoderWorker.terminate();
      logger('info', 'Worker terminated successfully');
    } catch (e) {
      logger('error', 'Error terminating worker', {
        error: e,
      });
    }

    logger('info', 'Finished web worker audio recording cleanup');

    audioEncoderWorker.removeEventListener('message', workerMessageHandler);
    audioEncoderWorker.removeEventListener('error', workerErrorHandler);
    isFinishedStop = true;

    safeSendBack({
      type: 'recorder.cleanup_finalized',
    });

    logger('info', 'Recording stopped, cleanup complete', {
      numAudioPacketsReceived: numAudioPacketsReceived,
      totalBytesReceived: totalBytesReceived,
      // Frame-health totals: capture gaps = real loss (device sleep/suspend);
      // delivery lag = jank/backpressure (audio intact); seq drops = transit loss.
      captureGapCount: captureGapStats.captureGapCount,
      totalCaptureGapMs: captureGapStats.totalCaptureGapMs,
      peakDeliveryLagMs: captureGapStats.peakDeliveryLagMs,
      droppedFrameCount: captureGapStats.droppedFrameCount,
    });
  };

  const startRecording = async () => {
    try {
      window.eluveMediaStreams.stop('sessionRecorder');
      audioEncoderWorker.addEventListener('message', workerMessageHandler);

      // Getting the media stream from the audio input device
      // Explicitly disable browser audio processing (AGC, echo cancellation,
      // noise suppression) — these distort single-speaker ambient recording
      mediaStream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints(deviceId),
      );

      // Validate MediaStream tracks
      const audioTracks = mediaStream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error('No audio tracks available in MediaStream');
      }

      audioTracks.forEach((track) => {
        if (!track.enabled || track.readyState !== 'live') {
          logger('warn', 'Audio track is not active', {
            enabled: track.enabled,
            readyState: track.readyState,
            label: track.label,
          });
        }
      });

      const audioSourceConfig: AudioSourceConfig = {
        mediaStream,
        worker: audioEncoderWorker,
        logger,
        MuteDetector,
        deviceId,
        channelCount,
        onMute: handleMuteEvent,
        onUnmute: handleUnmuteEvent,
        onBytesReceived,
        audioPacketSubject,
        onTrackEnded: onMicrophoneDisconnectDetected,
      };

      // Create and initialize audio source based on mode
      if (mode === 'pcm') {
        audioSource = new PcmAudioSource(audioSourceConfig);
      } else {
        audioSource = new BlobAudioSource({
          ...audioSourceConfig,
          mimeType: supportedAudioFormat.mimeType,
        });
      }

      // Initialize audio source and get actual audio parameters
      const { sampleRate, channels } = await audioSource.initialize();

      recordingStarted = true;

      const loggingEnv = datadogEnv ?? 'local-dev';
      const isLocalDev = loggingEnv === 'local-dev';

      // Gate `recorder.started` on the worker's `ready` message: a worker
      // whose script 404'd or crashed never processes `init`, and without
      // this gate the session reports started while recording nothing.
      // Created before `init` is posted so a fast reply can't be missed.
      // [ENG-5289]
      const workerReady: Promise<void> = workerBootError
        ? Promise.reject(workerBootError)
        : new Promise<void>((resolve, reject) => {
            settleWorkerReady = { resolve, reject };
          });

      // Initialize the worker
      audioEncoderWorker.postMessage({
        type: 'init',
        mode,
        dirPath: [
          userId,
          tenantId,
          ...(appointmentId ? ['appointments', appointmentId] : []),
        ],
        chunking: chunkDurationInSeconds ? { chunkDurationInSeconds } : false,
        opusBitrate,
        sampleRate,
        channels,
        recordingStartedAt,
        initialFileName,
        // Blob mode stores the recorder's container (fMP4 on Safari) — minted
        // names must match it. PCM always muxes WebM.
        fileExtension:
          mode === 'blob' ? supportedAudioFormat.extension : 'webm',
        resumableUploadSessionUri,
        nextChunkResumableUploadSessionUri,
        loggingConfig: isLocalDev
          ? { type: 'loki' }
          : {
              type: 'datadog',
              clientToken: 'pub1f5d27dc59ce54610ad0852ff58ac9f2',
              site: 'us5.datadoghq.com',
              service: serviceName ?? 'web-vite',
              env: loggingEnv,
            },
        tenantId,
        userId,
        appointmentId,
      } satisfies MsgFromMain);

      let workerReadyTimer: ReturnType<typeof setTimeout> | null = null;
      try {
        await Promise.race([
          workerReady,
          new Promise<never>((_, reject) => {
            workerReadyTimer = setTimeout(
              () =>
                reject(
                  new Error(
                    `Audio encoder worker did not report ready within ${WORKER_READY_TIMEOUT_MS}ms`,
                  ),
                ),
              WORKER_READY_TIMEOUT_MS,
            );
          }),
        ]);
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        logger(
          'error',
          'Encoder worker never became ready — failing recording start',
          {
            errorMessage: err.message,
            mode,
          },
        );
        safeSendBack({ type: 'recorder.error', error: err });
        return;
      } finally {
        if (workerReadyTimer) {
          clearTimeout(workerReadyTimer);
        }
        settleWorkerReady = null;
      }

      window.eluveMediaStreams.set('sessionRecorder', mediaStream);

      audioHealthCheck.start();

      // Monitor page visibility — on iOS Safari, backgrounding kills
      // MediaStream tracks. Detect this so the UI can warn the user.
      visibilityMonitor = new PageVisibilityMonitor({
        onHidden: () => {
          logger(
            'warn',
            'Page hidden during recording — audio may be interrupted on mobile',
          );
        },
        onVisible: (hiddenDurationMs) => {
          logger('info', 'Page visible again after recording backgrounded', {
            hiddenDurationMs,
          });
        },
        onRecordingInterrupted: () => {
          logger(
            'warn',
            'Recording interrupted — audio tracks died while page was hidden',
          );
          onMicrophoneDisconnectDetected();
        },
        getMediaStream: () => mediaStream,
        logger,
      });
      visibilityMonitor.start();

      safeSendBack({
        type: 'recorder.started',
        mediaStream,
        waveformAnalyserNode: audioSource.waveformAnalyserNode ?? undefined,
        audioParams: { sampleRate, channels, encodeMode: mode },
      });
    } catch (error) {
      // When the selected device ID is not available, treat it as a disconnection
      if (error instanceof Error && error.name === 'OverconstrainedError') {
        const isDeviceIdConstraint =
          ('constraints' in error && error.constraints === 'deviceId') ||
          ('constraint' in error && error.constraint === 'deviceId');
        if (isDeviceIdConstraint) {
          onMicrophoneDisconnectDetected();
          return;
        }
      }

      safeSendBack({
        type: 'recorder.error',
        error: new Error(
          `An error occurred while getting stream from audio input device. Error: ${(error as Error)?.message}`,
        ),
      });
    }
  };

  startRecording();

  receive((event) => {
    if (event.type === 'next_chunk_resumable_upload_session_uri_available') {
      audioEncoderWorker.postMessage(event satisfies MsgFromMain);
    }
    if (event.type === 'resumable_upload_session_uri_available') {
      audioEncoderWorker.postMessage(event satisfies MsgFromMain);
    }
    if (event.type === 'recorder.stop') {
      stopRecording();
      return;
    }
  });

  return () => {
    if (!isFinishedStop) {
      logger(
        'warn',
        'Web Worker Audio Actor cleanup invoked without stop. This could indicate a race condition',
      );
      stopRecording();
    }
  };
});
